/**
 * AppLock — PIN (4-digit) + Biometric (WebAuthn platform authenticator) lock screen
 * PIN is set by admin and stored as SHA-256 hash in Firestore app_config/lock
 * Biometric uses the device's native fingerprint / Face ID via WebAuthn
 * Spacemancraft Pvt Ltd
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const db = () => getFirestore(getApp());

const T = {
  bg:     '#090c14',
  panel:  '#111827',
  panel2: '#0e1420',
  border: '#1e2d45',
  bright: '#eef2ff',
  dim:    '#4a5a78',
  muted:  '#6b7fa0',
  accent: '#6366f1',
  green:  '#10b981',
  red:    '#f43f5e',
  yellow: '#f59e0b',
  mono:   "'JetBrains Mono', monospace",
  sans:   "'Inter', system-ui, sans-serif",
};

async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── WebAuthn credential storage key ──────────────────────────────────────────
const CRED_KEY = 'smc_bio_cred_id';

async function registerBiometric(userId) {
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'Spacemancraft', id: window.location.hostname },
        user: {
          id: new TextEncoder().encode(userId),
          name: userId,
          displayName: 'Spacemancraft User',
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60000,
      },
    });
    if (cred) {
      const credId = btoa(String.fromCharCode(...new Uint8Array(cred.rawId)));
      localStorage.setItem(CRED_KEY, credId);
      return true;
    }
  } catch (_) {}
  return false;
}

async function verifyBiometric() {
  try {
    const credIdB64 = localStorage.getItem(CRED_KEY);
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const opts = {
      publicKey: {
        challenge,
        userVerification: 'required',
        timeout: 60000,
        allowCredentials: credIdB64 ? [{
          type: 'public-key',
          id: Uint8Array.from(atob(credIdB64), c => c.charCodeAt(0)),
          transports: ['internal'],
        }] : [],
      },
    };
    const assertion = await navigator.credentials.get(opts);
    return !!assertion;
  } catch (_) {
    return false;
  }
}

// ── PIN Admin Setup Panel ─────────────────────────────────────────────────────
export function PinSetupPanel({ userId }) {
  const [pin,    setPin]    = useState('');
  const [pin2,   setPin2]   = useState('');
  const [saved,  setSaved]  = useState(false);
  const [err,    setErr]    = useState('');
  const [busy,   setBusy]   = useState(false);
  const [bioOk,  setBioOk]  = useState(false);
  const [bioReg, setBioReg] = useState(false);
  const [enabled,    setEnabled]    = useState(true);
  const [timeoutMin, setTimeoutMin] = useState(10);
  const [cfgMsg,     setCfgMsg]     = useState('');

  // Lock on/off + auto-lock delay (org-wide, same doc as the PIN)
  useEffect(() => {
    getDoc(doc(db(), 'app_config', 'lock')).then(snap => {
      const d = snap.exists() ? snap.data() : {};
      setEnabled(d.enabled !== false);
      setTimeoutMin(Number(d.timeoutMin) || 10);
    }).catch(() => {});
  }, []);

  async function saveLockConfig(next) {
    const cfg = { enabled, timeoutMin, ...next };
    setEnabled(cfg.enabled); setTimeoutMin(cfg.timeoutMin);
    try {
      await setDoc(doc(db(), 'app_config', 'lock'),
        { enabled: cfg.enabled, timeoutMin: cfg.timeoutMin, updatedBy: userId, updatedAt: new Date().toISOString() },
        { merge: true });
      setCfgMsg(cfg.enabled ? `✓ App lock ON · locks after ${cfg.timeoutMin} min idle` : '✓ App lock OFF for everyone');
    } catch (e) { setCfgMsg(`Could not save: ${e.message}`); }
  }

  useEffect(() => {
    setBioReg(!!localStorage.getItem(CRED_KEY));
    if (window.PublicKeyCredential) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(ok => setBioOk(ok)).catch(() => {});
    }
  }, []);

  async function savePin(e) {
    e.preventDefault();
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) { setErr('PIN must be exactly 4 digits'); return; }
    if (pin !== pin2) { setErr('PINs do not match'); return; }
    setBusy(true);
    const hash = await sha256(pin);
    await setDoc(doc(db(), 'app_config', 'lock'), { pinHash: hash, setBy: userId, setAt: new Date().toISOString() }, { merge: true });
    setSaved(true); setErr(''); setBusy(false);
    setPin(''); setPin2('');
  }

  async function handleBioRegister() {
    const ok = await registerBiometric(userId);
    setBioReg(ok);
    if (!ok) setErr('Biometric registration failed. Try again.');
  }

  return (
    <div style={{ padding: '16px 20px', background: T.panel2, border: `1px solid ${T.border}`,
      borderRadius: 10, maxWidth: 360 }}>
      <div style={{ fontFamily: T.mono, fontSize: 9, color: T.accent, letterSpacing: 2, marginBottom: 14 }}>
        🔐 APP LOCK SETTINGS
      </div>

      {/* Enable / disable + auto-lock delay */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '10px 12px', marginBottom: 12, borderRadius: 8,
        background: enabled ? `${T.green}10` : `${T.red}10`,
        border: `1px solid ${enabled ? `${T.green}44` : `${T.red}44`}` }}>
        <div>
          <div style={{ fontFamily: T.mono, fontSize: 10, color: enabled ? T.green : T.red, letterSpacing: 1 }}>
            APP LOCK {enabled ? 'ENABLED' : 'DISABLED'}
          </div>
          <div style={{ fontFamily: T.sans, fontSize: 11, color: T.muted, marginTop: 2 }}>
            {enabled ? 'Asks for the PIN after inactivity' : 'Nobody is asked for a PIN'}
          </div>
        </div>
        <button type="button" role="switch" aria-checked={enabled}
          onClick={() => saveLockConfig({ enabled: !enabled })}
          style={{ width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative',
            background: enabled ? T.green : T.border, flexShrink: 0, transition: 'background .15s' }}>
          <span style={{ position: 'absolute', top: 3, left: enabled ? 23 : 3, width: 20, height: 20, borderRadius: '50%',
            background: '#fff', transition: 'left .15s' }} />
        </button>
      </div>
      {enabled && (
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: 1, marginBottom: 12 }}>
          AUTO-LOCK AFTER
          <select value={timeoutMin} onChange={e => saveLockConfig({ timeoutMin: Number(e.target.value) })}
            style={{ background: T.panel, border: `1px solid ${T.border}`, color: T.bright, borderRadius: 6,
              padding: '5px 8px', fontFamily: T.mono, fontSize: 10 }}>
            {[2, 5, 10, 15, 30, 60].map(m => <option key={m} value={m}>{m} min idle</option>)}
          </select>
        </label>
      )}
      {cfgMsg && (
        <div style={{ fontFamily: T.mono, fontSize: 9, color: cfgMsg.startsWith('✓') ? T.green : T.red, marginBottom: 12 }}>
          {cfgMsg}
        </div>
      )}
      {saved && (
        <div style={{ padding: '8px 12px', background: `${T.green}12`, border: `1px solid ${T.green}33`,
          borderRadius: 6, fontFamily: T.mono, fontSize: 9, color: T.green, marginBottom: 12 }}>
          ✓ PIN saved successfully
        </div>
      )}
      {err && (
        <div style={{ padding: '8px 12px', background: `${T.red}12`, border: `1px solid ${T.red}33`,
          borderRadius: 6, fontFamily: T.mono, fontSize: 9, color: T.red, marginBottom: 12 }}>
          {err}
        </div>
      )}
      <form onSubmit={savePin} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
          value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="New 4-digit PIN"
          style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 6,
            color: T.bright, padding: '8px 12px', fontFamily: T.mono, fontSize: 16,
            letterSpacing: 8, outline: 'none', textAlign: 'center' }} />
        <input type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
          value={pin2} onChange={e => setPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="Confirm PIN"
          style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 6,
            color: T.bright, padding: '8px 12px', fontFamily: T.mono, fontSize: 16,
            letterSpacing: 8, outline: 'none', textAlign: 'center' }} />
        <button type="submit" disabled={busy || pin.length !== 4 || pin2.length !== 4}
          style={{ padding: '9px 0', fontFamily: T.mono, fontSize: 10, letterSpacing: 1,
            background: `${T.accent}18`, border: `1px solid ${T.accent}`, color: T.accent,
            cursor: 'pointer', borderRadius: 6 }}>
          {busy ? 'SAVING…' : 'SET PIN'}
        </button>
      </form>

      {bioOk && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginBottom: 8 }}>
            BIOMETRIC UNLOCK
          </div>
          {bioReg ? (
            <div style={{ fontFamily: T.mono, fontSize: 9, color: T.green }}>
              ✓ Fingerprint / Face ID registered
            </div>
          ) : (
            <button type="button" onClick={handleBioRegister}
              style={{ padding: '8px 16px', fontFamily: T.mono, fontSize: 9, letterSpacing: 1,
                background: `${T.green}12`, border: `1px solid ${T.green}44`, color: T.green,
                cursor: 'pointer', borderRadius: 6 }}>
              👆 REGISTER FINGERPRINT / FACE ID
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Numpad key ────────────────────────────────────────────────────────────────
function Key({ label, onClick, wide, danger }) {
  const [pressed, setPressed] = useState(false);
  if (label === '') return <div />;
  return (
    <button type="button"
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => { setPressed(false); onClick?.(); }}
      onPointerLeave={() => setPressed(false)}
      style={{
        height: 64, borderRadius: wide ? 32 : '50%',
        gridColumn: wide ? 'span 1' : undefined,
        background: pressed
          ? (danger ? `${T.red}30` : `${T.accent}30`)
          : (danger ? `${T.red}10` : T.panel2),
        border: `1px solid ${pressed
          ? (danger ? T.red : T.accent)
          : (danger ? `${T.red}44` : T.border)}`,
        color: danger ? T.red : T.bright,
        fontSize: label === '⌫' ? 20 : 24, fontWeight: 600,
        cursor: 'pointer', transition: 'background 0.08s, border-color 0.08s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        userSelect: 'none', WebkitUserSelect: 'none',
        touchAction: 'manipulation',
      }}>
      {label}
    </button>
  );
}

// ── Main Lock Screen ──────────────────────────────────────────────────────────
export default function AppLock({ onUnlock, logo }) {
  const [pin,        setPin]        = useState('');
  const [error,      setError]      = useState('');
  const [shake,      setShake]      = useState(false);
  const [bioOk,      setBioOk]      = useState(false);
  const [bioBusy,    setBioBusy]    = useState(false);
  const [pinHash,    setPinHash]    = useState(null);
  const [noPinSet,   setNoPinSet]   = useState(false);
  const [attempts,   setAttempts]   = useState(0);
  const [locked,     setLocked]     = useState(false); // locked out after 5 fails
  const lockTimerRef = useRef(null);

  // Load PIN hash and check biometric availability
  useEffect(() => {
    getDoc(doc(db(), 'app_config', 'lock')).then(snap => {
      if (snap.exists() && snap.data().pinHash) {
        setPinHash(snap.data().pinHash);
      } else {
        setNoPinSet(true); // No PIN set — unlock immediately
        onUnlock();
      }
    }).catch(() => {
      setNoPinSet(true);
      onUnlock(); // On Firestore error, don't block the user
    });

    if (window.PublicKeyCredential) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(ok => { if (ok && localStorage.getItem(CRED_KEY)) setBioOk(true); })
        .catch(() => {});
    }
  }, [onUnlock]);

  // Auto-trigger biometric if available
  useEffect(() => {
    if (bioOk && pinHash) handleBiometric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bioOk, pinHash]);

  const doShake = useCallback((msg) => {
    setError(msg); setShake(true); setPin('');
    setTimeout(() => setShake(false), 600);
  }, []);

  async function handleBiometric() {
    if (bioBusy || locked) return;
    setBioBusy(true);
    const ok = await verifyBiometric();
    setBioBusy(false);
    if (ok) { onUnlock(); }
    else setError('Biometric failed — enter PIN');
  }

  async function pressKey(key) {
    if (locked) return;
    setError('');
    if (key === '⌫') { setPin(p => p.slice(0, -1)); return; }

    const newPin = pin + key;
    setPin(newPin);

    if (newPin.length === 4) {
      // Short delay so 4th dot is visible
      await new Promise(r => setTimeout(r, 120));
      const hash = await sha256(newPin);
      if (hash === pinHash) {
        onUnlock();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 5) {
          setLocked(true);
          doShake('Too many attempts. Try again in 30s.');
          lockTimerRef.current = setTimeout(() => {
            setLocked(false); setAttempts(0); setError('');
          }, 30000);
        } else {
          doShake(`Incorrect PIN (${5 - newAttempts} attempts left)`);
        }
      }
    }
  }

  useEffect(() => () => { if (lockTimerRef.current) clearTimeout(lockTimerRef.current); }, []);

  if (noPinSet) return null;

  const KEYS = [
    ['1','2','3'],
    ['4','5','6'],
    ['7','8','9'],
    [bioOk ? '👆' : '', '0', '⌫'],
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: T.bg, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: T.sans, padding: '0 24px',
      userSelect: 'none',
    }}>
      {/* Logo */}
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#000', borderRadius: 16, padding: '10px 20px' }}>
        <img src="/logo-dark.png" alt="Spacemancraft"
          style={{ height: 56, width: 'auto', objectFit: 'contain' }} />
      </div>

      {/* Title */}
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, letterSpacing: 3, marginBottom: 32 }}>
        ENTER PIN TO UNLOCK
      </div>

      {/* PIN dots */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24,
        transform: shake ? 'translateX(0)' : undefined,
        animation: shake ? 'shake 0.5s ease' : undefined,
      }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{
            width: 16, height: 16, borderRadius: '50%',
            background: pin.length > i ? T.accent : 'transparent',
            border: `2px solid ${pin.length > i ? T.accent : T.border}`,
            transition: 'background 0.1s, border-color 0.1s',
            boxShadow: pin.length > i ? `0 0 8px ${T.accent}80` : 'none',
          }} />
        ))}
      </div>

      {/* Error / Status */}
      <div style={{
        minHeight: 22, marginBottom: 20, fontFamily: T.mono, fontSize: 10,
        color: locked ? T.red : T.red, textAlign: 'center',
        opacity: error ? 1 : 0, transition: 'opacity 0.2s',
      }}>
        {error}
      </div>

      {/* Numpad */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 64px)',
        gap: 14, marginBottom: 24,
        opacity: locked ? 0.4 : 1, transition: 'opacity 0.3s',
      }}>
        {KEYS.flat().map((k, i) => (
          <Key key={i} label={k}
            danger={k === '⌫'}
            onClick={k === '👆' ? handleBiometric : k !== '' ? () => pressKey(k) : undefined}
          />
        ))}
      </div>

      {/* Biometric button (shown separately if not in numpad) */}
      {bioOk && (
        <button type="button" onClick={handleBiometric} disabled={bioBusy || locked}
          style={{
            padding: '10px 28px', fontFamily: T.mono, fontSize: 10, letterSpacing: 2,
            background: `${T.green}12`, border: `1px solid ${T.green}44`,
            color: bioBusy ? T.muted : T.green, cursor: 'pointer', borderRadius: 8,
            opacity: locked ? 0.4 : 1,
          }}>
          {bioBusy ? 'VERIFYING…' : '👆 USE FINGERPRINT / FACE ID'}
        </button>
      )}

      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-10px); }
          40%      { transform: translateX(10px); }
          60%      { transform: translateX(-6px); }
          80%      { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
